import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import auditService from '../../../services/auditService';
import type { Store } from '../../../services/authService';
import { loadStoreDropdownOptions } from '../../../services/storeDropdownService';
import { useAudit } from '../../../hooks/useAudit';
import showToast from '../../../utils/toastUtils';
import type { ReceiptAuditsParams, UploadReceiptResponse } from '../../../services/auditService';
import { parsePositiveInt } from '../../../utils/normalizers/numberNormalizers';
import {
  AUDIT_AGE_DAYS_PARAM,
  AUDIT_DATE_FROM_PARAM,
  AUDIT_DATE_TO_PARAM,
  AUDIT_PAGE_PARAM,
  AUDIT_REPORT_TYPE_PARAM,
  AUDIT_SIZE_PARAM,
  AUDIT_SORT_BY_PARAM,
  AUDIT_SORT_ORDER_PARAM,
  AUDIT_STATUS_PARAM,
  AUDIT_STORE_ID_PARAM,
} from '../utils/auditQueryParams';
import { FIX_OCR_STORE_ID_PARAM } from '../fixOcr/utils/fixOcrQueryParams';
import type { AuditSortKey } from '../../../types/features/auditListing';
import { sortReceiptAudits } from '../../../utils/auditSorting';

const DEFAULT_STATUS = 'None';
const DEFAULT_AGE_DAYS = 7;
const ALLOWED_STATUSES: ReceiptAuditsParams['status'][] = [
  'pending',
  'processed',
  'finalized',
  'auto_created',
  'auto_finalized',
  'ready',
  'failed',
  'processing',
  'completed',
  'queued',
  'rejected',
  'None',
];

const buildPendingDateRange = (searchParams: URLSearchParams): [Date | null, Date | null] => [
  searchParams.get(AUDIT_DATE_FROM_PARAM) ? new Date(searchParams.get(AUDIT_DATE_FROM_PARAM)!) : null,
  searchParams.get(AUDIT_DATE_TO_PARAM) ? new Date(searchParams.get(AUDIT_DATE_TO_PARAM)!) : null,
];

const buildInitialFilters = (searchParams: URLSearchParams): ReceiptAuditsParams => {
  const filters: ReceiptAuditsParams = {};
  const storeId = searchParams.get(AUDIT_STORE_ID_PARAM);
  const status = searchParams.get(AUDIT_STATUS_PARAM);
  const reportType = searchParams.get(AUDIT_REPORT_TYPE_PARAM);
  const dateFrom = searchParams.get(AUDIT_DATE_FROM_PARAM);
  const dateTo = searchParams.get(AUDIT_DATE_TO_PARAM);
  const ageDays = parsePendingAgeDays(searchParams);

  if (storeId) {
    filters.store_id = storeId;
  }

  if (status && status !== 'None') {
    filters.status = status as ReceiptAuditsParams['status'];
  }

  if (reportType) {
    filters.report_type = reportType as ReceiptAuditsParams['report_type'];
  }

  if (dateFrom) {
    filters.date_from = dateFrom;
  }

  if (dateTo) {
    filters.date_to = dateTo;
  }

  filters.age_days = ageDays;

  return filters;
};

const parsePendingStatus = (searchParams: URLSearchParams): string =>
  searchParams.get(AUDIT_STATUS_PARAM) ?? DEFAULT_STATUS;

const parsePendingAgeDays = (searchParams: URLSearchParams): number => {
  const parsed = Number.parseInt(searchParams.get(AUDIT_AGE_DAYS_PARAM) || '', 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_AGE_DAYS;
};

const parseAuditSortKey = (value: string | null): AuditSortKey => {
  if (value === 'createdAt' || value === 'processedAt' || value === 'shiftDateTime') {
    return value;
  }

  return 'shiftDateTime';
};

const parseAuditSortOrder = (value: string | null): 'asc' | 'desc' =>
  value === 'asc' || value === 'desc' ? value : 'desc';

export const useAuditPageLogic = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [pendingStoreFilter, setPendingStoreFilter] = useState<string>(searchParams.get(AUDIT_STORE_ID_PARAM) || '');
  const [pendingStatusFilter, setPendingStatusFilter] = useState<string>(parsePendingStatus(searchParams));
  const [pendingReportTypeFilter, setPendingReportTypeFilter] = useState<string>(
    searchParams.get(AUDIT_REPORT_TYPE_PARAM) || ''
  );
  const [pendingDateRange, setPendingDateRange] = useState<[Date | null, Date | null]>(
    buildPendingDateRange(searchParams)
  );
  const [pendingAgeDays, setPendingAgeDays] = useState<number>(parsePendingAgeDays(searchParams));
  const [orderBy, setOrderBy] = useState<AuditSortKey>(() =>
    parseAuditSortKey(searchParams.get(AUDIT_SORT_BY_PARAM))
  );
  const [order, setOrder] = useState<'asc' | 'desc'>(() =>
    parseAuditSortOrder(searchParams.get(AUDIT_SORT_ORDER_PARAM))
  );

  const initialFiltersRef = useRef<ReceiptAuditsParams | null>(null);
  const initialPageRef = useRef<number | null>(null);
  const initialSizeRef = useRef<number | null>(null);
  if (!initialFiltersRef.current) {
    initialFiltersRef.current = buildInitialFilters(searchParams);
  }
  if (initialPageRef.current === null) {
    initialPageRef.current = parsePositiveInt(searchParams.get(AUDIT_PAGE_PARAM), 1);
  }
  if (initialSizeRef.current === null) {
    initialSizeRef.current = parsePositiveInt(searchParams.get(AUDIT_SIZE_PARAM), 25);
  }

  const {
    audits,
    loading,
    error,
    total,
    page,
    size,
    refreshAudits,
    setFilters,
    setError,
    setPage,
    setSize,
  } = useAudit(initialFiltersRef.current, {
    initialPage: initialPageRef.current ?? 1,
    initialSize: initialSizeRef.current ?? 25,
  });

  const sortedAudits = useMemo(
    () => sortReceiptAudits(audits, orderBy, order),
    [audits, order, orderBy],
  );

  useEffect(() => {
    if (searchParams.get(AUDIT_STATUS_PARAM)) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(AUDIT_STATUS_PARAM, DEFAULT_STATUS);
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const updateURLParams = useCallback(
    (newFilters: ReceiptAuditsParams) => {
      const params = new URLSearchParams(searchParams);

      if (newFilters.store_id) {
        params.set(AUDIT_STORE_ID_PARAM, newFilters.store_id);
      } else {
        params.delete(AUDIT_STORE_ID_PARAM);
      }

      if (newFilters.status) {
        params.set(AUDIT_STATUS_PARAM, newFilters.status);
      } else {
        params.delete(AUDIT_STATUS_PARAM);
      }

      if (newFilters.report_type) {
        params.set(AUDIT_REPORT_TYPE_PARAM, newFilters.report_type);
      } else {
        params.delete(AUDIT_REPORT_TYPE_PARAM);
      }

      if (newFilters.date_from) {
        params.set(AUDIT_DATE_FROM_PARAM, newFilters.date_from);
      } else {
        params.delete(AUDIT_DATE_FROM_PARAM);
      }

      if (newFilters.date_to) {
        params.set(AUDIT_DATE_TO_PARAM, newFilters.date_to);
      } else {
        params.delete(AUDIT_DATE_TO_PARAM);
      }

      if (newFilters.age_days !== undefined) {
        params.set(AUDIT_AGE_DAYS_PARAM, String(newFilters.age_days));
      } else {
        params.delete(AUDIT_AGE_DAYS_PARAM);
      }

      params.set(AUDIT_PAGE_PARAM, '1');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (location.state?.successMessage) {
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
        },
        { replace: true },
      );
    }
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    const loadStores = async () => {
      try {
        setStoresLoading(true);
        setStores(await loadStoreDropdownOptions());
      } catch (err) {
        console.error('Failed to load stores:', err);
        showToast.error('Failed to load stores. Please try again.');
      } finally {
        setStoresLoading(false);
      }
    };

    loadStores();
  }, []);

  useEffect(() => {
    setPendingStoreFilter(searchParams.get(AUDIT_STORE_ID_PARAM) || '');
    setPendingStatusFilter(parsePendingStatus(searchParams));
    setPendingReportTypeFilter(searchParams.get(AUDIT_REPORT_TYPE_PARAM) || '');
    setPendingDateRange(buildPendingDateRange(searchParams));
    setPendingAgeDays(parsePendingAgeDays(searchParams));
    setOrderBy(parseAuditSortKey(searchParams.get(AUDIT_SORT_BY_PARAM)));
    setOrder(parseAuditSortOrder(searchParams.get(AUDIT_SORT_ORDER_PARAM)));
  }, [searchParams]);

  const handleUploadSuccess = useCallback(
    (_response: UploadReceiptResponse) => {
      refreshAudits();
    },
    [refreshAudits]
  );

  const handleClearFilters = useCallback(() => {
    setPendingStoreFilter('');
    setPendingStatusFilter('None');
    setPendingReportTypeFilter('');
    setPendingDateRange([null, null]);
    setPendingAgeDays(DEFAULT_AGE_DAYS);
    setFilters({ age_days: DEFAULT_AGE_DAYS });
    const params = new URLSearchParams(searchParams);
    params.delete(AUDIT_STORE_ID_PARAM);
    params.set(AUDIT_STATUS_PARAM, 'None');
    params.delete(AUDIT_REPORT_TYPE_PARAM);
    params.delete(AUDIT_DATE_FROM_PARAM);
    params.delete(AUDIT_DATE_TO_PARAM);
    params.set(AUDIT_AGE_DAYS_PARAM, String(DEFAULT_AGE_DAYS));
    params.set(AUDIT_PAGE_PARAM, '1');
    setSearchParams(params, { replace: true });
  }, [searchParams, setFilters, setSearchParams]);

  const handlePageChange = useCallback(
    (_event: unknown, newPage: number) => {
      setPage(newPage);
      const params = new URLSearchParams(searchParams);
      params.set(AUDIT_PAGE_PARAM, String(newPage));
      setSearchParams(params, { replace: true });
    },
    [searchParams, setPage, setSearchParams]
  );

  const handleRowsPerPageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newSize = parseInt(event.target.value, 10) || 25;
      setSize(newSize);
      const params = new URLSearchParams(searchParams);
      params.set(AUDIT_SIZE_PARAM, String(newSize));
      params.set(AUDIT_PAGE_PARAM, '1');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams, setSize]
  );

  const handleRequestSort = useCallback((key: AuditSortKey) => {
    const nextOrder: 'asc' | 'desc' = orderBy === key && order === 'asc' ? 'desc' : 'asc';
    setPage(1);
    setOrder(nextOrder);
    setOrderBy(key);

    const params = new URLSearchParams(searchParams);
    params.set(AUDIT_SORT_BY_PARAM, key);
    params.set(AUDIT_SORT_ORDER_PARAM, nextOrder);
    params.set(AUDIT_PAGE_PARAM, '1');
    setSearchParams(params, { replace: true });
  }, [order, orderBy, searchParams, setPage, setSearchParams]);

  const handleFixOCR = useCallback(
    (queueId: string, storeId: string, _alreadyProcess: boolean) => {
      const params = new URLSearchParams(location.search);
      params.set(FIX_OCR_STORE_ID_PARAM, storeId);
      params.delete('storeId');
      const search = params.toString();
      const url = `/dashboard/audit/fix-ocr/${queueId}${search ? `?${search}` : ''}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [location.search]
  );

  const handleRevertToReady = useCallback(
    async (queueId: string) => {
      try {
        const response = await auditService.revertToReady(queueId);
        showToast.success(response.message || 'Audit reverted to ready successfully');
        refreshAudits();
      } catch (error: any) {
        console.error('Error reverting to ready:', error);
        showToast.error(error?.message || 'Failed to revert audit to ready');
      }
    },
    [refreshAudits]
  );

  const handleReprocess = useCallback(
    async (queueId: string) => {
      try {
        const response = await auditService.reprocessAudit(queueId);
        if (response.success) {
          showToast.success(response.message || 'Audit reprocessing started successfully');
          refreshAudits();
        } else {
          showToast.error(response.message || 'Failed to reprocess audit');
        }
      } catch (error: any) {
        console.error('Error reprocessing audit:', error);
        showToast.error(error?.message || 'Failed to reprocess audit');
      }
    },
    [refreshAudits]
  );

  return {
    model: {
      audits,
      sortedAudits,
      loading,
      error,
      total,
      page,
      size,
      stores,
      storesLoading,
      pendingStoreFilter,
      pendingStatusFilter,
      pendingReportTypeFilter,
      pendingDateRange,
      pendingAgeDays,
      uploadDialogOpen,
      allowedStatuses: ALLOWED_STATUSES,
      orderBy,
      order,
    },
    actions: {
      setPendingStoreFilter,
      setPendingStatusFilter,
      setPendingReportTypeFilter,
      setPendingDateRange,
      setPendingAgeDays,
      setFilters,
      setError,
      onClearError: () => setError(null),
      updateURLParams,
      onUploadSuccess: handleUploadSuccess,
      onClearFilters: handleClearFilters,
      onPageChange: handlePageChange,
      onRowsPerPageChange: handleRowsPerPageChange,
      onRequestSort: handleRequestSort,
      onFixOCR: handleFixOCR,
      onRevertToReady: handleRevertToReady,
      onReprocess: handleReprocess,
      onRefreshAudits: refreshAudits,
      onOpenUploadDialog: () => setUploadDialogOpen(true),
      onCloseUploadDialog: () => setUploadDialogOpen(false),
      onNavigateToCreate: () => navigate('/dashboard/audit/create'),
    },
  };
};
